import * as React from 'react';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { Translate } from '../../../../../../../shared/LanguageService';
import ProductPropertiesIcons from '../../../../../../propertyIcons/ProductPropertiesIcons';
import { IOrderRow } from '../../../Entities';
import {
    ImageEditorPhotoStatus,
    OrderRowQuantityInfo
} from '../../entry/queries/use-query-photo-status';
import { getDeliveryProgress } from '../state/order-row-summary';

const HeaderContainer = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    backgroundColor: '#f5f5f5',
    border: '1px solid #ddd',
    padding: '12px 16px',
    fontSize: '18px',
    lineHeight: '30px'
}) as typeof Box;

const HeaderContent = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    flex: '1 1 auto'
});

const TitleRow = styled('div')({
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '6px'
});

const ProductName = styled(Typography)({
    fontSize: '18px',
    fontWeight: 600
}) as typeof Typography;

const OrderedCount = styled('span')({
    color: '#9e9e9e'
});

type OrderRowHeaderProps = {
    orderRow: IOrderRow;
    photoStatus: ImageEditorPhotoStatus;
    additionalProducts: OrderRowQuantityInfo[];
    actions?: React.ReactNode;
};

function orderedImagesText(numberOfImages: number): string {
    return `(${Translate('unedited-images/number-of-images', { numberOfImages })})`;
}

export function OrderRowHeader({
    orderRow,
    photoStatus,
    additionalProducts,
    actions
}: OrderRowHeaderProps) {
    let deliveryProgress = getDeliveryProgress(orderRow, photoStatus, additionalProducts);

    return (
        <HeaderContainer component="header">
            <HeaderContent>
                <TitleRow>
                    <ProductName variant="h2" component="h2">
                        {orderRow.name.trim()}
                    </ProductName>
                    <OrderedCount>{orderedImagesText(orderRow.numberOfImages)}</OrderedCount>
                    <ProductPropertiesIcons properties={orderRow.orderRowProductProperties} />
                    {additionalProducts.map((product) => (
                        <span key={product.orderRowId}>
                            {' + '}
                            <strong>{product.productName.trim()}</strong>{' '}
                            <OrderedCount>
                                {orderedImagesText(product.expectedQuantity)}
                            </OrderedCount>
                        </span>
                    ))}
                </TitleRow>
                {deliveryProgress && (
                    <div>
                        {`${deliveryProgress.delivered}/${deliveryProgress.ordered} ${Translate(
                            'image-processing/page/order-row/header/ordered-images'
                        )}`}
                    </div>
                )}
            </HeaderContent>
            {actions}
        </HeaderContainer>
    );
}

export function OrderRowHeaderSkeleton() {
    return (
        <HeaderContainer
            component="header"
            aria-busy="true"
            data-testid="order-row-header-skeleton"
        >
            <HeaderContent>
                <TitleRow>
                    <Skeleton
                        variant="text"
                        animation="wave"
                        width={200}
                        sx={{ fontSize: '18px' }}
                    />
                    <Skeleton variant="rounded" animation="wave" width={40} height={20} />
                </TitleRow>
            </HeaderContent>
            <Skeleton variant="rounded" animation="wave" width={150} height={36} />
        </HeaderContainer>
    );
}
